import { useState } from "react";
import { usePost } from "./API";
import { TailwindSwitch } from "./Components/Switch";
import { ClickableButton } from "./Components/Button";
import { mapStrToList } from "./Util";
import { Button, Modal } from "@aidkitorg/component-library";

export default function BatchSetPage() {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [uids, setUids] = useState('');
  const [batchSetError, setBatchSetError] = useState('');

  const batchSet = usePost('/admin/batch_set');

  // Copy Field -- copy one key to another for selected applicants
  const [oldKey, setOldKey] = useState('');
  const [newKey, setNewKey] = useState('');

  const [copyFieldUIDs, setCopyFieldUIDs] = useState('');

  const copyField = usePost('/admin/copy_field');

  const [hideUnhide, setHideUnhide] = useState('hide');
  const [hideUnhideUIDs, setHideUnhideUIDs] = useState('');
  const batchHideUnhide = usePost('/admin/batch_hide_unhide');

  // Batch Delete Field
  const [deleteFieldKey, setDeleteFieldKey] = useState('');
  const [deleteFieldUIDs, setDeleteFieldUIDs] = useState('');
  const [deleteFieldModalOpen, setDeleteFieldModalOpen] = useState(false);
  const [deleteFieldConfirmedUnused, setDeleteFieldConfirmedUnused] = useState(false);
  const [deleteFieldConfirmedNoAuditValue, setDeleteFieldConfirmedNoAuditValue] = useState(false);
  const [deleteFieldRetypeKey, setDeleteFieldRetypeKey] = useState('');
  const [deleteFieldParsedUIDs, setDeleteFieldParsedUIDs] = useState<string[]>([]);
  const batchDeleteField = usePost('/admin/batch_delete_field');

  const [deleteFacesDry, setDeleteFacesDry] = useState(true);
  const deleteFacesForHiddenAndTestApplicants = usePost('/admin/delete_hidden_and_test_app_faces');
  const [deleteFacesResult, setDeleteFacesResult] = useState<{ open: boolean, content: string }>({ open: false, content: '' });

  const [bool_doCompute, setBool_doCompute] = useState(false);

  async function submitBatchSet() {
    setBatchSetError('');
    const result = await batchSet({
      key,
      value,
      applicantUids: mapStrToList(uids),
      compute: bool_doCompute
    });
    // `batchSet` only declares `validationError`/`success` in its return type, but a thrown
    // server error (e.g. a missing key) comes back as `{ error }` at runtime, so check for it
    // explicitly instead of treating every non-validationError response as a success.
    const genericError = (result as { error?: unknown }).error;
    const computeError = (result as { computeError?: unknown }).computeError;
    if (result.validationError) {
      setBatchSetError(result.validationError);
    } else if (genericError) {
      setBatchSetError(typeof genericError === 'string' ? genericError : JSON.stringify(genericError));
    } else if (computeError) {
      // Unlike validationError/error above, the applicantinfo writes here already committed --
      // only the optional recompute step failed. Don't show the "no applicants were updated"
      // copy, since that would be false.
      alert(`Batch written successfully, but the recompute step failed: ${computeError}. Applicant data was updated; only derived/computed values may be stale.`);
    } else {
      alert('Success');
    }
  }

  async function submitCopyField() {
    alert(JSON.stringify(await copyField({
      oldKey,
      newKey,
      uids: mapStrToList(copyFieldUIDs)
    })))
  }

  async function submitHideUnhide() {
    alert(JSON.stringify(await batchHideUnhide({
      uids: mapStrToList(hideUnhideUIDs),
      hide: hideUnhide === 'hide'
    })))
  }

  function openDeleteFieldModal() {
    setDeleteFieldConfirmedUnused(false);
    setDeleteFieldConfirmedNoAuditValue(false);
    setDeleteFieldRetypeKey('');
    setDeleteFieldParsedUIDs(mapStrToList(deleteFieldUIDs));
    setDeleteFieldModalOpen(true);
  }

  async function submitDeleteField() {
    const result = await batchDeleteField({
      key: deleteFieldKey.trim(),
      ...(deleteFieldParsedUIDs.length > 0 && { applicantUids: deleteFieldParsedUIDs }),
    });
    setDeleteFieldModalOpen(false);
    setDeleteFieldKey('');
    setDeleteFieldUIDs('');
    alert(JSON.stringify(result));
  }

  async function submitDeleteTestAndHiddenFaces() {
    if (!deleteFacesDry && !confirm('Permanently delete face data from Rekognition for all hidden and test applicants in this program? This cannot be undone.')) {
      return;
    }
    const response = await deleteFacesForHiddenAndTestApplicants({ dry: deleteFacesDry });
    setDeleteFacesResult({ open: true, content: JSON.stringify(response, null, 2) });
  }

  return <>
    <h1>Batch Operations</h1>
    <hr/>
    <h2>Batch Set</h2>
    <p>Note that for simplicity and performance this does not run RS. If you want to run RS, do so from the other admin interface</p>

    <fieldset>
      <legend>Key</legend>
      <input value={key} onChange={(e) => setKey(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <legend>Value</legend>
      <input value={value} onChange={(e) => setValue(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <legend>UIDs (comma, line, or space separated)</legend>
      <textarea value={uids} onChange={(e) => setUids(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <TailwindSwitch checked={bool_doCompute} onChange={() => setBool_doCompute(!bool_doCompute)} 
        label="Recompute after?" />
    </fieldset>
    <br/>
    <ClickableButton onClick={submitBatchSet} color="gray">Update</ClickableButton>

    {batchSetError && (
      <div className="mt-4">
        <div>Batch set failed and no applicants were updated (the whole batch is rolled back on any error). Fix the issue below, then resubmit the entire batch:</div>
        <div className="text-red-500 whitespace-pre-line">{batchSetError}</div>
      </div>
    )}

    <hr/>
    <h2>Copy Field</h2>
    <p>This copies one field to another for all applicants using their existing value for that field.</p>
    <fieldset>
      <legend>Copy From Target Field (Old Key)</legend>
      <input value={oldKey} onChange={(e) => setOldKey(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <legend>Copy To Target Field (New Key)</legend>
      <input value={newKey} onChange={(e) => setNewKey(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <legend>UIDs (comma, line, or space separated)</legend>
      <textarea value={copyFieldUIDs} onChange={(e) => setCopyFieldUIDs(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <button onClick={submitCopyField}>Update</button>

    <hr/>
    <h2>Batch Hide/Unhide</h2>
    <p>This will hide or unhide all the applicants you provide below. Good way to quarantine applicants. Be sure to unhide after hiding if they are intended to stick around.</p>

    <fieldset>
      <legend>Hide or Unhide?</legend>
      <label className="block"><input type="radio" name="hide" onClick={() => setHideUnhide('hide')} value={'hide'} /> Hide</label>
      <label><input type="radio" name="hide" onClick={() => setHideUnhide('unhide')} value={'unhide'} /> Unhide</label>
    </fieldset>
    <fieldset>
      <legend>UIDs (comma, line, or space separated)</legend>
      <textarea value={hideUnhideUIDs} onChange={(e) => setHideUnhideUIDs(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <button onClick={submitHideUnhide}>{hideUnhide === 'hide' ? 'Hide' : 'Unhide'} Applicants</button>

    <hr/>
    <h2>Batch Delete Field</h2>
    <p>Permanently deletes all historical records for a field key. If UIDs are provided, only those applicants are affected; otherwise all applicants in the program are affected. This cannot be undone.</p>
    <p>All current values for the field must be set to empty string before deleting. Use Batch Set above to clear them first.</p>

    <fieldset>
      <legend>Field Key</legend>
      <input value={deleteFieldKey} onChange={(e) => setDeleteFieldKey(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <fieldset>
      <legend>UIDs (comma, line, or space separated)</legend>
      <textarea value={deleteFieldUIDs} onChange={(e) => setDeleteFieldUIDs(e.target.value)} className="max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md" />
    </fieldset>
    <br/>
    <ClickableButton
      onClick={openDeleteFieldModal}
      color="red"
      disabled={!deleteFieldKey.trim()}
    >
      Delete Field
    </ClickableButton>

    <Modal
      open={deleteFieldModalOpen}
      onClose={() => setDeleteFieldModalOpen(false)}
      title="Confirm Field Deletion"
      preventBackdropClick
    >
      <div className="mt-4 space-y-4">
        <p className="text-sm text-gray-700">
          You are about to permanently delete all records for the field{' '}
          <code className="bg-gray-100 px-1 rounded font-mono">{deleteFieldKey}</code> for{' '}
          {deleteFieldParsedUIDs.length > 0
            ? <><strong>{deleteFieldParsedUIDs.length}</strong> applicant(s)</>
            : <strong>all applicants in this program</strong>
          }. This cannot be undone.
        </p>

        <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
          <input
            type="checkbox"
            checked={deleteFieldConfirmedUnused}
            onChange={(e) => setDeleteFieldConfirmedUnused(e.target.checked)}
            className="mt-0.5 size-4 flex-shrink-0 rounded border-2 border-blue-800 accent-blue-800"
          />
          I confirm this field is not actively used anywhere in the program configuration (surveys, eligibility rules, computed fields, etc.)
        </label>

        <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
          <input
            type="checkbox"
            checked={deleteFieldConfirmedNoAuditValue}
            onChange={(e) => setDeleteFieldConfirmedNoAuditValue(e.target.checked)}
            className="mt-0.5 size-4 flex-shrink-0 rounded border-2 border-blue-800 accent-blue-800"
          />
          I confirm having a historic record of this field provides no value to the program for auditing or compliance purposes.
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Re-type the field key to confirm: <code className="bg-gray-100 px-1 rounded font-mono">{deleteFieldKey}</code>
          </label>
          <input
            value={deleteFieldRetypeKey}
            onChange={(e) => setDeleteFieldRetypeKey(e.target.value)}
            placeholder={deleteFieldKey}
            className="max-w-lg block w-full shadow-sm border border-gray-300 p-2 sm:text-sm rounded-md"
          />
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={() => setDeleteFieldModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isDisabled={!deleteFieldConfirmedUnused || !deleteFieldConfirmedNoAuditValue || deleteFieldRetypeKey !== deleteFieldKey.trim()}
            onClick={submitDeleteField}
          >
            Delete Field
          </Button>
        </div>
      </div>
    </Modal>

    <hr/>
    <h2>Delete Face Data for Hidden &amp; Test Applicants</h2>
    <p>Removes indexed faces from the AWS Rekognition collection for every applicant in this program where <code>applicant.hidden = true</code> or <code>test_applicant = 'yes'</code>.</p>
    <p>Does not modify <code>_face</code>/<code>_duplicates</code>/<code>_related</code>/<code>_unindexed_reason</code>/<code>_conversion</code> rows in <code>applicantinfo</code>; pair with Batch Set if you need those cleared.</p>
    <p>This also means that the number of "matchedApplicants" and "deleted" in the response will not always be equal, but that does not mean the deletion was unsuccessful.</p>

    <fieldset>
      <TailwindSwitch checked={deleteFacesDry} onChange={() => setDeleteFacesDry(!deleteFacesDry)}
        label="Dry run (no changes made)" />
    </fieldset>
    <br/>
    <ClickableButton onClick={submitDeleteTestAndHiddenFaces} color={deleteFacesDry ? 'gray' : 'red'}>{deleteFacesDry ? 'Preview' : 'Delete'} Face Data</ClickableButton>

    <Modal
      open={deleteFacesResult.open}
      onClose={() => setDeleteFacesResult((prev) => ({ ...prev, open: false }))}
      title={deleteFacesDry ? 'Delete Face Data (Dry Run)' : 'Delete Face Data Result'}
      size="lg"
    >
      <pre className="mt-4 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs whitespace-pre-wrap break-words">{deleteFacesResult.content}</pre>
    </Modal>
  </>
}
